import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 24px 0;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 8px;
  overflow: hidden;
`;

const Thead = styled.thead`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#F8FAFC'};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#F1F5F9'};

  &:last-child {
    border-bottom: none;
  }
`;

const PropName = styled.code`
  font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
  color: #FA4616;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.1)'
      : 'rgba(250, 70, 22, 0.05)'};
  padding: 2px 6px;
  border-radius: 4px;
`;

const PropType = styled.code`
  font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  font-size: 12px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#F8FAFC'};
  padding: 2px 6px;
  border-radius: 4px;
`;

const PropDefault = styled.code`
  font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  font-size: 12px;
  color: ${(props) => (props.theme.name === 'dark' ? '#22C55E' : '#059669')};
`;

const PropDescription = styled.div`
  line-height: 1.5;
`;

const RequiredBadge = styled.span`
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  color: #EF4444;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(239, 68, 68, 0.1)'};
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

/**
 * PropsTable - Display component props documentation in a table format
 *
 * @param {Object} props
 * @param {Array} props.props - Array of prop objects with name, type, default, required, and description
 *
 * @example
 * <PropsTable props={[
 *   {
 *     name: 'variant',
 *     type: 'string',
 *     default: '"default"',
 *     required: false,
 *     description: 'Button style variant'
 *   }
 * ]} />
 */
const PropsTable = ({ props }) => {
  if (!props || props.length === 0) {
    return null;
  }

  return (
    <Table>
      <Thead>
        <tr>
          <Th>Prop</Th>
          <Th>Type</Th>
          <Th>Default</Th>
          <Th>Description</Th>
        </tr>
      </Thead>
      <tbody>
        {props.map((prop) => (
          <tr key={prop.name}>
            <Td>
              <PropName>{prop.name}</PropName>
              {prop.required && <RequiredBadge>Required</RequiredBadge>}
            </Td>
            <Td>
              <PropType>{prop.type}</PropType>
            </Td>
            <Td>
              {prop.default ? (
                <PropDefault>{prop.default}</PropDefault>
              ) : (
                <span style={{ color: '#94A3B8', fontSize: '12px' }}>—</span>
              )}
            </Td>
            <Td>
              <PropDescription>{prop.description}</PropDescription>
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

PropsTable.propTypes = {
  props: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      default: PropTypes.string,
      required: PropTypes.bool,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default PropsTable;